import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canPushToTally } from "@/lib/tallyAccess";
import { roleAllowed } from "@/lib/roles";
import { buildCustomerMap } from "@/lib/customerNames";
import { dcGinProgress, dcLineGinCode, dcLinePendingQty, dcLineReceivedQty, fmtQty, hasPendingGinLines, isDcLineReceived } from "@/lib/dcGinProgress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Truck, Send, CheckCircle2, Printer, Trash2, Download, FileCheck2, FileCode, UploadCloud, RotateCcw, PackageSearch, Search, Pencil, Save, X, Receipt, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import ReceivedPartPicker from "@/components/ReceivedPartPicker";
import DcExportPanel from "@/components/DcExportPanel";
import PdiReportDialog, { PDI_STATUS } from "@/components/PdiReportDialog";
import { exportPdiReportPdf } from "@/lib/pdiReportPdf";

// A raised DC that has not been dispatched yet is "ready" — older records
// still carry the former name "draft" until the server migrates them.
const isDcReady = (status) => status === "ready" || status === "draft";
const dcStatusLabel = (status) => (isDcReady(status) ? "ready" : status);

const STATUS = {
  ready: "bg-sky-50 text-sky-700",
  draft: "bg-sky-50 text-sky-700",   // legacy name for "ready" (migrated server-side)
  dispatched: "bg-amber-50 text-amber-700",
  delivered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function DeliveryChallansPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [sos, setSos] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ received_qtys: [], received_item_ids: [], target_stores: [], notes: "" });
  const [reverseOpen, setReverseOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Rate/Disc %/GST %/Amount for the selected DC's lines — a DC line itself
  // carries none of this (see DeliveryChallanLine), so it's fetched from the
  // customer Sales Order this DC was raised against whenever a customer-
  // outward DC is opened. Indexed to line up 1:1 with `selected.lines`.
  const [linePricing, setLinePricing] = useState([]);
  // Whether this user may override Rate/Disc %/GST % on the open DC (admin
  // plus the backend's DC_PRICING_EDITOR_EMAILS; false once invoiced) —
  // returned by .../pricing-lines so the list lives in one place.
  const [pricingCanEdit, setPricingCanEdit] = useState(false);
  const [pricingDraft, setPricingDraft] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);

  // Each flag matches its endpoint's require_role list exactly, including
  // role aliases (see roleAllowed) — e.g. "sales_crm" is aliased to "planner"
  // on the backend, so it must count as "planner" here too, or the button
  // stays hidden for a role the API would happily accept.
  const canDispatch = roleAllowed(user?.role, ["admin", "planner", "operator"]);
  // Reversing a dispatched/delivered DC undoes live stock + order-status
  // effects — matches POST /delivery-challans/{id}/reverse-and-delete (admin-only).
  const canReverse = user?.role === "admin";
  const canPushTally = canPushToTally(user);
  // Matches the roles POST /delivery-challans/{id}/receive-goods accepts.
  const canReceiveGoods = roleAllowed(user?.role, ["admin", "planner", "stores", "quality", "production_manager"]);
  // Matches the roles POST /delivery-challans/{id}/generate-tax-invoice accepts.
  const canGenerateInvoice = roleAllowed(user?.role, ["admin", "planner", "operator"]);
  // PDI Report (Final Inspection Report) — roles ticked on Users & Roles
  // (GET /pdi-reports/access); the status badge shows for everyone.
  const [canPdi, setCanPdi] = useState(false);
  const [pdiStatus, setPdiStatus] = useState(null);
  const [pdiOpen, setPdiOpen] = useState(false);

  // "customer" = a customer DC — raised from a Sales Order (with or without
  // a Packing Slip), so this sheet just points there; "subcontract" = manual
  // entry against a Supplier/Vendor (Phase — manual DC creation).
  const [recipientType, setRecipientType] = useState("customer");
  // A blank Supplier/Vendor DC. "Manual Entry" lets a one-time supplier or a
  // one-time item (not in the Supplier / Items master) be typed in — those
  // details are saved on this DC only, never added to the masters.
  const EMPTY_VENDOR_LINE = { item_id: "", qty: 1, is_manual: false, part_name: "", part_code: "", uom: "Nos", rate: "" };
  const EMPTY_VENDOR_FORM = {
    vendor_id: "", vendor_is_manual: false, vendor_name: "", vendor_contact_person: "", vendor_phone: "", vendor_address: "",
    vehicle_no: "", driver_name: "", transporter: "", dispatch_date: "", notes: "",
    lines: [EMPTY_VENDOR_LINE],
  };
  const [vendorForm, setVendorForm] = useState(EMPTY_VENDOR_FORM);

  const load = async () => {
    const [d, s, i, c, n, sup] = await Promise.all([
      api.get("/delivery-challans"), api.get("/sales-orders"), api.get("/items"), api.get("/customers"),
      api.get("/customers/names-lookup"),
      api.get("/suppliers").catch(() => ({ data: [] })),
    ]);
    setList(d.data);
    setSos(s.data);
    setItems(i.data);
    setCustomers(c.data);
    setCustomerNames(n.data);
    setSuppliers(sup.data);
    if (selected) setSelected(d.data.find((x) => x.id === selected.id) || null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get("/pdi-reports/access").then((r) => setCanPdi(!!r.data.can_access)).catch(() => setCanPdi(false));
  }, []);
  useEffect(() => {
    let cancelled = false;
    setPdiStatus(null);
    if (!selected || selected.kind !== "customer") return;
    api.get(`/pdi-reports/dc/${selected.id}/status`)
      .then((r) => { if (!cancelled) setPdiStatus(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selected]);

  // Deep-link support — "View DC" from the (customer-facing) Sales Order page.
  useEffect(() => {
    const id = location.state?.selectId;
    if (id && list.length) {
      const found = list.find((x) => x.id === id);
      if (found) setSelected(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectId, list.length]);

  // Rate/Disc %/GST %/Amount for the open DC's lines — only customer-outward
  // DCs have the customer + agreed Sales Order pricing this needs; a
  // subcontract (job-work) DC has neither, so its Lines table skips these
  // columns entirely rather than showing an error.
  useEffect(() => {
    let cancelled = false;
    setPricingDraft(null);
    if (!selected || selected.kind !== "customer") { setLinePricing([]); setPricingCanEdit(false); return; }
    api.get(`/delivery-challans/${selected.id}/pricing-lines`)
      .then((r) => { if (!cancelled) { setLinePricing(r.data.lines || []); setPricingCanEdit(!!r.data.can_edit); } })
      .catch(() => { if (!cancelled) { setLinePricing([]); setPricingCanEdit(false); } });
    return () => { cancelled = true; };
  }, [selected]);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const custMap = useMemo(() => buildCustomerMap(customers, customerNames), [customers, customerNames]);
  const soMap = useMemo(() => Object.fromEntries(sos.map((s) => [s.id, s])), [sos]);
  // Customer DCs carry their order's dispatch position (see
  // _attach_dc_display_fields) — an order can go out over several DCs.
  const showCumulative = selected?.kind !== "subcontract" && (selected?.lines || []).some((l) => l.cumulative_qty != null);
  const supMap = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.id, s])), [suppliers]);

  // A line's item may since have been deleted — fall back to the code/name
  // snapshotted on the DC line itself rather than showing a blank/dashed row.
  const lineCode = (l) => itemMap[l.item_id]?.code || l.part_code || "—";
  const lineName = (l) => itemMap[l.item_id]?.name || l.part_name || "—";

  // Who this DC was sent to — the customer for a Transfer-Order DC, the
  // vendor/supplier for a subcontract (job-work) DC.
  const recipientName = (dc) => dc.kind === "subcontract" ? (dc.vendor_name || "—") : (custMap[dc.customer_id]?.name || "—");

  // Search bar — matches on DC No. or the recipient (customer/vendor) name,
  // case-insensitive substring so partial typing narrows the list as you go.
  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((dc) => {
      const code = (dc.code || "").toLowerCase();
      const name = recipientName(dc).toLowerCase();
      return code.includes(q) || name.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, search, custMap, supMap]);

  // Enter jumps straight to the exact DC — matched by exact DC No. first,
  // then exact recipient name, falling back to the top of the filtered list.
  const onSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const q = search.trim().toLowerCase();
    if (!q) return;
    const exact =
      list.find((dc) => (dc.code || "").toLowerCase() === q) ||
      list.find((dc) => recipientName(dc).toLowerCase() === q) ||
      filteredList[0];
    if (exact) setSelected(exact);
  };

  // The order this DC is raised against is stored as SO-XXXX but reads to the
  // business as a Transfer Order — swap only the displayed prefix (never the
  // underlying code) so it shows as TO-XXXX wherever it's labeled "TO No.".
  const toNo = (code) => (code && code.startsWith("SO-") ? `TO-${code.slice(3)}` : code || "—");
  // Prefix the Employee Code of whoever raised that Transfer Order (stamped
  // on the DC by the backend as `to_employee_code`) ahead of the TO number,
  // e.g. "PA-TO-5023" — matches the DC PDF.
  const toNoWithEmployee = (dc) => {
    const no = toNo(dc?.so_code);
    return dc?.to_employee_code && no !== "—" ? `${dc.to_employee_code}-${no}` : no;
  };

  // --- Manual Supplier/Vendor (subcontract) DC creation ---
  const setVendorLine = (i, p) => setVendorForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...p } : l) }));
  const addVendorLine = () => setVendorForm((f) => ({ ...f, lines: [...f.lines, EMPTY_VENDOR_LINE] }));
  const removeVendorLine = (i) => setVendorForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const resetForms = () => {
    setVendorForm(EMPTY_VENDOR_FORM);
    setRecipientType("customer");
  };

  const createVendorDc = async () => {
    if (busy) return;
    if (vendorForm.vendor_is_manual ? !vendorForm.vendor_name.trim() : !vendorForm.vendor_id) {
      return toast.error(vendorForm.vendor_is_manual ? "Enter the Supplier Name" : "Pick a Supplier / Vendor");
    }
    const badManual = vendorForm.lines.find((l) => l.is_manual && Number(l.qty) > 0 && !l.part_name.trim());
    if (badManual) return toast.error("Enter the Part Description for every manual line");
    if (vendorForm.lines.some((l) => l.is_manual && l.rate !== "" && Number(l.rate) < 0)) return toast.error("Rate cannot be negative");
    const lines = vendorForm.lines
      .filter((l) => (l.is_manual ? l.part_name.trim() : l.item_id) && Number(l.qty) > 0)
      .map((l) => (l.is_manual
        ? { is_manual: true, part_name: l.part_name.trim(), part_code: l.part_code.trim(), uom: l.uom.trim(),
            rate: l.rate === "" ? null : Number(l.rate), qty: Number(l.qty) }
        : { item_id: l.item_id, qty: Number(l.qty) }));
    if (!lines.length) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const payload = {
        ...(vendorForm.vendor_is_manual
          ? { vendor_id: null, vendor_is_manual: true, vendor_name: vendorForm.vendor_name.trim(),
              vendor_contact_person: vendorForm.vendor_contact_person, vendor_phone: vendorForm.vendor_phone,
              vendor_address: vendorForm.vendor_address }
          : { vendor_id: vendorForm.vendor_id, vendor_name: supMap[vendorForm.vendor_id]?.name || "" }),
        vehicle_no: vendorForm.vehicle_no, driver_name: vendorForm.driver_name, transporter: vendorForm.transporter,
        dispatch_date: vendorForm.dispatch_date ? new Date(vendorForm.dispatch_date).toISOString() : null,
        notes: vendorForm.notes, lines,
      };
      await api.post("/delivery-challans/manual-subcontract", payload);
      toast.success("Delivery Challan created & dispatched");
      setOpen(false);
      resetForms();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const transition = async (dc, action, msg) => {
    setBusy(true);
    try {
      await api.post(`/delivery-challans/${dc.id}/${action}`);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const openReceive = (dc) => {
    setSelected(dc);
    // One received qty per DC line with qty still remaining, pre-filled with
    // that remaining qty — a line left at 0 stays pending (the DC stays
    // open) for a later GIN.
    setReceiveForm({
      received_qtys: (dc.lines || []).map((l) => (isDcLineReceived(dc, l) ? "" : dcLinePendingQty(l))),
      // Received Part Number (stage after job work) per line — required.
      received_item_ids: (dc.lines || []).map(() => ""),
      // Target Store override per line — blank = the received part's stage store.
      target_stores: (dc.lines || []).map(() => ""),
      notes: "",
    });
    setReceiveOpen(true);
  };

  const submitReceive = async () => {
    if (!selected) return;
    const lines = receiveForm.received_qtys
      .map((q, idx) => ({ dc_line_idx: idx, received_qty: Number(q || 0), received_item_id: receiveForm.received_item_ids[idx] || null, target_store: receiveForm.target_stores?.[idx] || "" }))
      .filter((l) => !isDcLineReceived(selected, selected.lines[l.dc_line_idx]));
    if (lines.some((l) => l.received_qty < 0)) return toast.error("Received quantity cannot be negative");
    if (!lines.some((l) => l.received_qty > 0)) return toast.error("Received quantity must be greater than 0");
    const over = lines.find((l) => l.received_qty > dcLinePendingQty(selected.lines[l.dc_line_idx]) + 1e-6);
    if (over) {
      const l = selected.lines[over.dc_line_idx];
      return toast.error(`${lineCode(l)}: received qty exceeds the remaining qty ${fmtQty(dcLinePendingQty(l))}`);
    }
    const noPart = lines.find((l) => l.received_qty > 0 && !l.received_item_id && !(selected.lines[l.dc_line_idx].is_manual && !selected.lines[l.dc_line_idx].item_id));
    if (noPart) return toast.error(`${lineCode(selected.lines[noPart.dc_line_idx])}: select the Received Part Number (stage after job work)`);
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-challans/${selected.id}/receive-goods`, { lines, notes: receiveForm.notes });
      const prog = dcGinProgress(data.dc);
      toast.success(data.dc.status === "delivered"
        ? `${selected.code} fully received · GIN ${data.gin.code} generated · DC closed`
        : `GIN ${data.gin.code} generated · ${fmtQty(prog.pendingQty)} qty still pending, DC stays open`);
      setReceiveOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Receive failed");
    } finally { setBusy(false); }
  };

  const generateInvoice = async (dc) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-challans/${dc.id}/generate-tax-invoice`, {});
      toast.success(`Tax Invoice ${data.code} generated`);
      await load();
      navigate("/tax-invoices", { state: { selectId: data.id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generate Tax Invoice failed");
    } finally { setBusy(false); }
  };

  const startPricingEdit = () => setPricingDraft((selected?.lines || []).map((_, idx) => {
    const p = linePricing[idx] || {};
    return { rate: String(p.rate ?? 0), discount_pct: String(p.discount_pct ?? 0), gst_pct: String(p.gst_pct ?? 0) };
  }));

  const setDraftField = (idx, field, value) =>
    setPricingDraft((d) => d.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  // Send only the fields that changed — an untouched field keeps whatever
  // it resolves to today (Sales Order value or an earlier override).
  const savePricing = async () => {
    const edits = [];
    for (const [idx, row] of pricingDraft.entries()) {
      const p = linePricing[idx] || {};
      const edit = { idx };
      for (const f of ["rate", "discount_pct", "gst_pct"]) {
        const v = Number(row[f]);
        if (row[f] === "" || Number.isNaN(v) || v < 0 || (f !== "rate" && v > 100)) {
          toast.error(`Line ${idx + 1}: invalid ${f === "rate" ? "Rate" : f === "discount_pct" ? "Disc %" : "GST %"}`);
          return;
        }
        if (Math.abs(v - Number(p[f] || 0)) > 1e-9) edit[f] = v;
      }
      if (Object.keys(edit).length > 1) edits.push(edit);
    }
    if (!edits.length) { setPricingDraft(null); return; }
    setPricingSaving(true);
    try {
      const r = await api.patch(`/delivery-challans/${selected.id}/line-pricing`, { lines: edits });
      setLinePricing(r.data.lines || []);
      setPricingCanEdit(!!r.data.can_edit);
      setPricingDraft(null);
      toast.success("Pricing updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update pricing");
    } finally {
      setPricingSaving(false);
    }
  };

  // Drop a line's overrides — back to the Sales Order's rate/discount/GST.
  const resetLinePricing = async (idx) => {
    try {
      const r = await api.patch(`/delivery-challans/${selected.id}/line-pricing`, {
        lines: [{ idx, rate: null, discount_pct: null, gst_pct: null }],
      });
      setLinePricing(r.data.lines || []);
      setPricingDraft(null);
      toast.success("Line reset to Sales Order pricing");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to reset pricing");
    }
  };

  const viewInvoice = (dc) => navigate("/tax-invoices", { state: { selectId: dc.tax_invoice_id } });
  // One Proforma Invoice per customer DC — pre-filled from the DC's lines
  // and pricing; once raised, the DC links to it instead.
  const viewProforma = (dc) => navigate("/proforma-invoices", { state: { selectId: dc.proforma_invoice_id } });
  const createProforma = async (dc) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-challans/${dc.id}/create-proforma-invoice`);
      toast.success(`Proforma Invoice ${data.code} created from ${dc.code}`);
      await load();
      navigate("/proforma-invoices", { state: { selectId: data.id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create Proforma Invoice");
    } finally { setBusy(false); }
  };
  const viewPackingSlip = (dc) => navigate("/packing-slips", { state: { selectId: dc.packing_slip_id } });

  // Print just the selected DC's formatted document (same layout as the PDF)
  // in its own window — plain window.print() would print this whole SPA
  // page (challans list, nav, everything currently on screen), not the one
  // challan the user has open.
  const printDc = async (dc) => {
    try {
      const r = await api.get(`/delivery-challans/${dc.id}/render-html`, { responseType: "text" });
      const w = window.open("", "_blank");
      w.document.write(`<html><head><title>${dc.code}</title></head><body>${r.data}<script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`);
      w.document.close();
    } catch {
      toast.error("Print failed");
    }
  };

  const downloadPdi = async (dc) => {
    try {
      const { data } = await api.get(`/pdi-reports/dc/${dc.id}`);
      exportPdiReportPdf(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not download PDI Report");
    }
  };

  // Export (download PDF) for one challan — used both on the detail panel
  // and as a per-row action in the list, so any DC can be exported without
  // first opening it.
  const downloadDcPdf = async (dc) => {
    try {
      const res = await api.get(`/delivery-challans/${dc.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${dc.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${dc.code}.pdf`);
    } catch {
      toast.error("PDF download failed");
    }
  };

  // Export — a Tally "Delivery Note" voucher XML, ready for Gateway of
  // Tally -> Import Data -> Vouchers (an .xlsx workbook, tried earlier,
  // isn't something Tally's importer accepts at all). Customer-outward DCs
  // only, since Rate/Discount/HSN need the customer's agreed Sales Order
  // pricing (resolved server-side), which a subcontract (job-work) DC has
  // no customer or pricing for at all.
  const exportDcTallyXml = async (dc) => {
    try {
      const res = await api.get(`/delivery-challans/${dc.id}/tally-xml`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `${dc.code}_tally.xml`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${dc.code}_tally.xml`);
    } catch {
      toast.error("Export failed");
    }
  };

  // Push — sends the same Delivery Note voucher straight to Tally (or to the
  // Tally Bridge Agent's queue, per Settings → Tally).
  const pushDcToTally = async (dc) => {
    setBusy(true);
    const push = (force) => api.post(`/delivery-challans/${dc.id}/tally-push`, null, { params: force ? { force: true } : {} });
    // Already in Tally → the server alters that voucher (never books a second
    // one). Only after an update failed because the voucher was deleted in
    // Tally is a new voucher offered.
    const asNew = (dc.tally_push_error || "").includes("Send as a new voucher") &&
      window.confirm(`Send ${dc.code} to Tally as a NEW voucher?\n\nOnly do this if you deleted its voucher in Tally — otherwise Tally will have it twice.`);
    try {
      const res = await push(asNew);
      toast.success(res.data?.queued ? `${dc.code} queued for the Tally Bridge Agent` : `${dc.code} sent to Tally`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Tally push failed");
      await load();
    } finally { setBusy(false); }
  };

  const remove = async (dc) => {
    if (!window.confirm(`Delete ${dc.code}?`)) return;
    try { await api.delete(`/delivery-challans/${dc.id}`); if (selected?.id === dc.id) setSelected(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitReverse = async (dc) => {
    setBusy(true);
    try {
      await api.post(`/delivery-challans/${dc.id}/reverse-and-delete`, {});
      toast.success(`${dc.code} reversed and deleted — stock and linked order status restored`);
      setReverseOpen(false);
      if (selected?.id === dc.id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reverse failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="dc-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Dispatch</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Delivery Challans
          </h1>
          <p className="text-sm text-slate-500 mt-1">Dispatch decrements FG stock and updates the source Transfer Order.</p>
        </div>
        <div className="flex items-center gap-2">
          <DcExportPanel />
          <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForms(); }}>
            <SheetTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-dc-btn"><Plus className="w-4 h-4 mr-1.5" />New DC</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
              <SheetHeader><SheetTitle>New Delivery Challan</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-4">
                <div>
                  <Label>Recipient</Label>
                  <div className="mt-1 grid grid-cols-2 gap-2" data-testid="dc-recipient-type">
                    <button type="button" onClick={() => setRecipientType("customer")} data-testid="dc-recipient-customer"
                      className={`text-sm px-3 py-2 rounded-md border text-left ${recipientType === "customer" ? "bg-blue-50 border-blue-300 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      <div className="font-semibold">Customer</div>
                      <div className="text-[11px] opacity-80">From a Sales Order</div>
                    </button>
                    <button type="button" onClick={() => setRecipientType("subcontract")} data-testid="dc-recipient-vendor"
                      className={`text-sm px-3 py-2 rounded-md border text-left ${recipientType === "subcontract" ? "bg-blue-50 border-blue-300 text-blue-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      <div className="font-semibold">Supplier / Vendor</div>
                      <div className="text-[11px] opacity-80">Manual entry — goods sent out for job work</div>
                    </button>
                  </div>
                </div>

                {recipientType === "customer" ? (
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-900 space-y-2" data-testid="dc-customer-flow-info">
                    <div className="font-semibold">Customer DCs are raised against a Sales Order</div>
                    <ol className="list-decimal ml-5 space-y-0.5 text-[13px]">
                      <li>Open the Sales Order and click Raise DC — enter the qty to send (up to what is still to be delivered). No Packing Slip is needed.</li>
                      <li>Or pack it first: create its Packing Slip, then Raise DC carries exactly the packed qty.</li>
                      <li>Dispatch the DC, then raise the Tax Invoice from it — it references the Sales Order.</li>
                    </ol>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label>Supplier / Vendor *</Label>
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                          <Checkbox checked={vendorForm.vendor_is_manual}
                            onCheckedChange={(v) => setVendorForm({ ...vendorForm, vendor_is_manual: !!v })}
                            data-testid="dc-vendor-manual-toggle" />
                          Manual Entry (One-time Supplier)
                        </label>
                      </div>
                      {vendorForm.vendor_is_manual ? (
                        <div className="mt-1 grid grid-cols-2 gap-3" data-testid="dc-vendor-manual-fields">
                          <div className="col-span-2">
                            <Label className="text-xs">Supplier Name *</Label>
                            <Input value={vendorForm.vendor_name} onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })} data-testid="dc-vendor-manual-name" />
                          </div>
                          <div>
                            <Label className="text-xs">Contact Person</Label>
                            <Input value={vendorForm.vendor_contact_person} onChange={(e) => setVendorForm({ ...vendorForm, vendor_contact_person: e.target.value })} data-testid="dc-vendor-manual-contact" />
                          </div>
                          <div>
                            <Label className="text-xs">Phone</Label>
                            <Input value={vendorForm.vendor_phone} onChange={(e) => setVendorForm({ ...vendorForm, vendor_phone: e.target.value })} data-testid="dc-vendor-manual-phone" />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Address</Label>
                            <Textarea rows={2} value={vendorForm.vendor_address} onChange={(e) => setVendorForm({ ...vendorForm, vendor_address: e.target.value })} data-testid="dc-vendor-manual-address" />
                          </div>
                          <div className="col-span-2 text-[11px] text-slate-500">Saved on this DC only — not added to the Supplier master.</div>
                        </div>
                      ) : (<>
                      <Select value={vendorForm.vendor_id} onValueChange={(v) => setVendorForm({ ...vendorForm, vendor_id: v })}>
                        <SelectTrigger data-testid="dc-vendor-select"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                        <SelectContent>
                          {suppliers.length === 0 && <SelectItem value="__none__" disabled>No suppliers — add one under Suppliers first</SelectItem>}
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {vendorForm.vendor_id && supMap[vendorForm.vendor_id] && (
                        <div className="mt-1.5 text-[11px] text-slate-500" data-testid="dc-vendor-summary">
                          {[supMap[vendorForm.vendor_id].address, supMap[vendorForm.vendor_id].city, supMap[vendorForm.vendor_id].state].filter(Boolean).join(", ") || "No address on file"}
                          {supMap[vendorForm.vendor_id].gstin ? ` · GSTIN ${supMap[vendorForm.vendor_id].gstin}` : ""}
                        </div>
                      )}
                      </>)}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Vehicle No.</Label><Input value={vendorForm.vehicle_no} onChange={(e) => setVendorForm({ ...vendorForm, vehicle_no: e.target.value })} data-testid="dc-vendor-vehicle-input" /></div>
                      <div><Label>Driver Name</Label><Input value={vendorForm.driver_name} onChange={(e) => setVendorForm({ ...vendorForm, driver_name: e.target.value })} /></div>
                      <div><Label>Transporter</Label><Input value={vendorForm.transporter} onChange={(e) => setVendorForm({ ...vendorForm, transporter: e.target.value })} /></div>
                      <div><Label>Dispatch Date</Label><Input type="date" value={vendorForm.dispatch_date} onChange={(e) => setVendorForm({ ...vendorForm, dispatch_date: e.target.value })} /></div>
                      <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={vendorForm.notes} onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })} /></div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-slate-700">Lines</div>
                        <Button variant="outline" size="sm" onClick={addVendorLine} data-testid="dc-vendor-add-line">
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {vendorForm.lines.map((l, idx) => (
                          <div key={idx} className={l.is_manual ? "rounded-md border border-amber-200 bg-amber-50/40 p-2" : ""}>
                            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer mb-1 w-fit">
                              <Checkbox checked={l.is_manual}
                                onCheckedChange={(v) => setVendorLine(idx, { is_manual: !!v })}
                                data-testid={`dc-vendor-line-manual-toggle-${idx}`} />
                              Manual Entry (One-time Item)
                            </label>
                            {l.is_manual ? (
                              <div className="grid grid-cols-12 gap-2 items-end" data-testid={`dc-vendor-line-manual-${idx}`}>
                                <div className="col-span-7">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Part Description *</Label>
                                  <Input value={l.part_name} onChange={(e) => setVendorLine(idx, { part_name: e.target.value })} data-testid={`dc-vendor-line-desc-${idx}`} />
                                </div>
                                <div className="col-span-4">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Part Number</Label>
                                  <Input value={l.part_code} onChange={(e) => setVendorLine(idx, { part_code: e.target.value })} data-testid={`dc-vendor-line-partno-${idx}`} />
                                </div>
                                <div className="col-span-1" />
                                <div className="col-span-3">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">UOM</Label>
                                  <Input value={l.uom} onChange={(e) => setVendorLine(idx, { uom: e.target.value })} data-testid={`dc-vendor-line-uom-${idx}`} />
                                </div>
                                <div className="col-span-4">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Qty *</Label>
                                  <Input type="number" step="0.01" value={l.qty} onChange={(e) => setVendorLine(idx, { qty: e.target.value })} data-testid={`dc-vendor-line-qty-${idx}`} />
                                </div>
                                <div className="col-span-4">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Rate</Label>
                                  <Input type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setVendorLine(idx, { rate: e.target.value })} data-testid={`dc-vendor-line-rate-${idx}`} />
                                </div>
                                <div className="col-span-1">
                                  <Button variant="ghost" size="icon" onClick={() => removeVendorLine(idx)} disabled={vendorForm.lines.length === 1}>
                                    <Trash2 className="w-4 h-4 text-slate-500" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-8">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Item</Label>
                                  <Select value={l.item_id} onValueChange={(v) => setVendorLine(idx, { item_id: v })}>
                                    <SelectTrigger data-testid={`dc-vendor-line-item-${idx}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                                    <SelectContent>
                                      {items.map((it) => (
                                        <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-3">
                                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Qty</Label>
                                  <Input type="number" step="0.01" value={l.qty} onChange={(e) => setVendorLine(idx, { qty: e.target.value })} data-testid={`dc-vendor-line-qty-${idx}`} />
                                </div>
                                <div className="col-span-1">
                                  <Button variant="ghost" size="icon" onClick={() => removeVendorLine(idx)} disabled={vendorForm.lines.length === 1}>
                                    <Trash2 className="w-4 h-4 text-slate-500" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <SheetFooter className="mt-6">
                {recipientType === "customer" ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate("/packing-slips")} data-testid="dc-goto-packing-slips">Packing Slips</Button>
                    <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/customer-sales-orders")} data-testid="dc-goto-sales-orders">Go to Sales Order</Button>
                  </div>
                ) : (
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={createVendorDc} disabled={busy} data-testid="create-dc-vendor-btn">Create & Dispatch DC</Button>
                )}
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Challans</span><span className="text-xs text-slate-500 mono">{search.trim() ? `${filteredList.length}/${list.length}` : list.length}</span>
          </div>
          <div className="px-4 py-2.5 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search by customer name or DC No."
                className="pl-8 h-9 text-sm"
                data-testid="dc-search-input"
              />
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {filteredList.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-dcs">
                {list.length === 0 ? "No delivery challans yet." : "No delivery challans match your search."}
              </div>
            )}
            {filteredList.map((dc) => (
              <div key={dc.id} className={`group relative ${selected?.id === dc.id ? "bg-blue-50/60" : ""}`}>
                <button onClick={() => setSelected(dc)} data-testid={`dc-row-${dc.code}`}
                  className="w-full text-left px-4 py-3 pr-11 hover:bg-slate-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{dc.code}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[dc.status]}`}>{dcStatusLabel(dc.status)}</span>
                  </div>
                  {dc.kind === "subcontract" && dc.status === "dispatched" && dcGinProgress(dc).anyReceived && (
                    <div className="mt-0.5 text-[10px] text-amber-700" data-testid={`dc-row-gin-progress-${dc.code}`}>
                      Partially received · {fmtQty(dcGinProgress(dc).receivedQty)}/{fmtQty(dcGinProgress(dc).sentQty)} qty · {fmtQty(dcGinProgress(dc).pendingQty)} pending
                    </div>
                  )}
                  <div className="mt-1 text-sm font-medium text-slate-800">
                    {recipientName(dc)}
                    {dc.vendor_is_manual && <span className="ml-1.5 text-[10px] font-normal uppercase tracking-wider text-amber-700">one-time</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {dc.kind === "subcontract"
                      ? <>Subcontract{dc.job_code ? <> · <span className="mono text-orange-700">{dc.job_code}</span></> : ""}</>
                      : <>From <span className="mono text-blue-700">{dc.so_code}</span></>}
                    {" "}· {dc.lines?.length || 0} line(s){dc.vehicle_no ? ` · ${dc.vehicle_no}` : ""}
                  </div>
                </button>
                <button
                  type="button"
                  title={`Export ${dc.code} as PDF`}
                  onClick={(e) => { e.stopPropagation(); downloadDcPdf(dc); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition"
                  data-testid={`dc-row-export-${dc.code}`}
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="dc-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <Truck className="w-10 h-10 text-slate-300" /> Select a delivery challan to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {recipientName(selected)}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {selected.kind === "subcontract"
                      ? <>Subcontract{selected.job_code ? <> · Job <span className="mono text-orange-700">{selected.job_code}</span></> : ""}{selected.stage_label ? ` · ${selected.stage_label}` : ""}</>
                      : <>Against Transfer Order <span className="mono text-blue-700">{toNoWithEmployee(selected)}</span></>}
                    {selected.dispatch_date ? ` · Dispatched ${new Date(selected.dispatch_date).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status]}`}>{dcStatusLabel(selected.status)}</span>
                  {selected.kind === "customer" && pdiStatus && (
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${PDI_STATUS[pdiStatus.status]?.cls || ""}`}
                      title={pdiStatus.status === "approved" ? `Approved by ${pdiStatus.approved_by_name} on ${pdiStatus.approval_date}` : undefined}
                      data-testid="dc-pdi-badge">
                      {PDI_STATUS[pdiStatus.status]?.label || pdiStatus.status}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selected.kind === "customer" && canPushTally && (
                    <Button size="sm" variant="outline" onClick={() => pushDcToTally(selected)} disabled={busy} data-testid="dc-tally-push-btn" title="Send this DC to Tally as a Delivery Note voucher">
                      <UploadCloud className="w-4 h-4 mr-1.5" /> Push to Tally
                    </Button>
                  )}
                  {selected.kind === "customer" && (
                    <Button size="sm" variant="outline" onClick={() => exportDcTallyXml(selected)} data-testid="dc-export-btn" title="Export as a Tally-importable Delivery Note voucher XML (Part No./HSN, Rate, Disc %, Amount)">
                      <FileCode className="w-4 h-4 mr-1.5" /> Export
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => downloadDcPdf(selected)} data-testid="dc-pdf-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Download PDF
                  </Button>
                </div>
              </div>

              {selected.kind === "customer" && selected.tally_push_status && (
                <div className={`text-xs px-3 py-2 rounded-md ${selected.tally_push_status === "pushed" ? "bg-emerald-50 text-emerald-700" : selected.tally_push_status === "queued" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`} data-testid="dc-tally-status">
                  Tally push {selected.tally_push_status}{selected.tally_pushed_at ? ` · ${new Date(selected.tally_pushed_at).toLocaleString()}` : ""}
                  {selected.tally_push_error ? ` — ${selected.tally_push_error}` : ""}
                </div>
              )}

              {/* Document trail — same order record is the "TO" (Transfer Order module,
                  pre-approval) and the "SO" (Sales Order, post Production Approval);
                  PO No. is the customer's PO reference captured on that order.
                  Subcontract DCs have no SO/PO — show the job + stage trail instead. */}
              {selected.kind === "subcontract" ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm bg-slate-50 border border-slate-100 rounded-md p-3" data-testid="dc-doc-refs">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">DC No.</div>
                    <div className="font-semibold mono text-slate-900">{selected.code}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Job No.</div>
                    <div className="font-semibold mono text-orange-700">{selected.job_code || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Stage</div>
                    <div className="font-semibold text-slate-900">{selected.stage_label || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Supplier / Vendor</div>
                    <div className="font-semibold text-slate-900">{selected.vendor_name || "—"}</div>
                    {selected.vendor_is_manual && <div className="text-[10px] text-amber-700" data-testid="dc-vendor-onetime">One-time supplier</div>}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm bg-slate-50 border border-slate-100 rounded-md p-3" data-testid="dc-doc-refs">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">DC No.</div>
                    <div className="font-semibold mono text-slate-900">{selected.code}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">TO No.</div>
                    <div className="font-semibold mono text-blue-700">{toNoWithEmployee(selected)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">SO No.</div>
                    <div className="font-semibold mono text-blue-700">{selected.customer_so_code || selected.so_code || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">PO No.</div>
                    <div className="font-semibold mono text-slate-900">{soMap[selected.so_id]?.po_reference || "—"}</div>
                  </div>
                  {selected.packing_slip_code && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Packing Slip</div>
                      <button type="button" onClick={() => viewPackingSlip(selected)} className="font-semibold mono text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="dc-ps-link">
                        <PackageSearch className="w-3.5 h-3.5" />{selected.packing_slip_code}
                      </button>
                      {selected.packing_batch_no && (
                        <div className="text-xs text-slate-500" data-testid="dc-packing-batch">Packing batch {selected.packing_batch_no}</div>
                      )}
                    </div>
                  )}
                  {selected.dc_seq_no && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Dispatch</div>
                      <div className="font-semibold text-slate-900" data-testid="dc-seq">
                        DC {selected.dc_seq_no} of {selected.order_dc_count}
                      </div>
                      <div className="text-xs text-slate-500" data-testid="dc-cumulative">
                        Cumulative <span className="mono">{selected.cumulative_qty}</span> / <span className="mono">{selected.ordered_qty}</span> ordered
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap" data-testid="dc-actions">
                {isDcReady(selected.status) && canDispatch && (
                  <Button onClick={() => transition(selected, "dispatch", `${selected.code} dispatched · stock updated`)} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="dc-dispatch-btn">
                    <Send className="w-4 h-4 mr-1.5" /> Dispatch
                  </Button>
                )}
                {selected.status === "dispatched" && canReceiveGoods && selected.kind === "subcontract" && hasPendingGinLines(selected) && (
                  <Button onClick={() => openReceive(selected)} disabled={busy} variant="outline" data-testid="dc-receive-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Receive Goods
                  </Button>
                )}
                {selected.status === "dispatched" && canDispatch && selected.kind !== "subcontract" && (
                  <Button onClick={() => transition(selected, "deliver", `${selected.code} marked delivered`)} disabled={busy} variant="outline" data-testid="dc-deliver-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Delivered
                  </Button>
                )}
                {selected.kind === "customer" && ["dispatched", "delivered"].includes(selected.status) && (
                  selected.tax_invoice_id ? (
                    <Button variant="outline" onClick={() => viewInvoice(selected)} data-testid="dc-view-invoice-btn">
                      <FileCheck2 className="w-4 h-4 mr-1.5" /> View Invoice {selected.tax_invoice_code}
                    </Button>
                  ) : canGenerateInvoice && (
                    <Button onClick={() => generateInvoice(selected)} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="dc-generate-invoice-btn">
                      <FileCheck2 className="w-4 h-4 mr-1.5" /> Generate Tax Invoice
                    </Button>
                  )
                )}
                {selected.kind === "customer" && selected.status !== "cancelled" && (
                  selected.proforma_invoice_id ? (
                    <Button variant="outline" onClick={() => viewProforma(selected)} data-testid="dc-view-pi-btn">
                      <Receipt className="w-4 h-4 mr-1.5" /> View Proforma {selected.proforma_invoice_code}
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => createProforma(selected)} disabled={busy} data-testid="dc-create-pi-btn">
                      <Receipt className="w-4 h-4 mr-1.5" /> Create Proforma Invoice
                    </Button>
                  )
                )}
                {selected.kind === "customer" && canPdi && (
                  <>
                    <Button variant="outline" onClick={() => setPdiOpen(true)} data-testid="dc-pdi-btn">
                      <ClipboardCheck className="w-4 h-4 mr-1.5" /> PDI Report
                    </Button>
                    {pdiStatus && pdiStatus.status !== "not_started" && (
                      <Button variant="outline" onClick={() => downloadPdi(selected)} data-testid="dc-pdi-download-btn">
                        <Download className="w-4 h-4 mr-1.5" /> Download PDI Report
                      </Button>
                    )}
                  </>
                )}
                <Button variant="outline" onClick={() => printDc(selected)} data-testid="dc-print-btn">
                  <Printer className="w-4 h-4 mr-1.5" /> Print
                </Button>
                {isDcReady(selected.status) && (
                  <Button variant="ghost" size="icon" onClick={() => remove(selected)} className="ml-auto" data-testid="dc-delete-btn">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
                {["dispatched", "delivered"].includes(selected.status) && canReverse && !selected.gin_id && !selected.tax_invoice_id && (
                  <Button
                    variant="outline"
                    onClick={() => setReverseOpen(true)}
                    className={`text-red-600 hover:text-red-700 ${isDcReady(selected.status) ? "" : "ml-auto"}`}
                    data-testid="dc-reverse-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Reverse &amp; Delete
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Vehicle</div>
                  <div className="font-medium mono">{selected.vehicle_no || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Driver</div>
                  <div className="font-medium">{selected.driver_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Transporter</div>
                  <div className="font-medium">{selected.transporter || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{selected.kind === "subcontract" ? "Vendor GSTIN" : "Customer GSTIN"}</div>
                  <div className="font-medium mono">
                    {selected.kind === "subcontract" ? (supMap[selected.vendor_id]?.gstin || "—") : (custMap[selected.customer_id]?.gstin || "—")}
                  </div>
                </div>
                {selected.vendor_is_manual && (
                  <>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Contact Person</div>
                      <div className="font-medium" data-testid="dc-vendor-contact">{selected.vendor_contact_person || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Vendor Phone</div>
                      <div className="font-medium mono" data-testid="dc-vendor-phone">{selected.vendor_phone || "—"}</div>
                    </div>
                  </>
                )}
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic">{selected.notes}</div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Lines</div>
                  {selected.kind === "customer" && pricingCanEdit && (
                    pricingDraft ? (
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setPricingDraft(null)} disabled={pricingSaving} data-testid="dc-pricing-cancel-btn">
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={savePricing} disabled={pricingSaving} data-testid="dc-pricing-save-btn">
                          <Save className="w-4 h-4 mr-1" /> Save pricing
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={startPricingEdit} data-testid="dc-pricing-edit-btn">
                        <Pencil className="w-4 h-4 mr-1" /> Edit pricing
                      </Button>
                    )
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                      <th className="py-2 px-3">Part No</th>
                      <th className="py-2 px-3">Item</th>
                      <th className="py-2 px-3 text-right">Qty</th>
                      {showCumulative && (
                        <>
                          <th className="py-2 px-3 text-right">Ordered</th>
                          <th className="py-2 px-3 text-right" title="This DC plus every earlier DC against the same order">Dispatched to date</th>
                          <th className="py-2 px-3 text-right">Balance</th>
                        </>
                      )}
                      {selected.kind === "customer" && (
                        <>
                          <th className="py-2 px-3 text-right">Rate</th>
                          <th className="py-2 px-3 text-right">Disc %</th>
                          <th className="py-2 px-3 text-right">GST %</th>
                          <th className="py-2 px-3 text-right">Amount</th>
                        </>
                      )}
                      <th className="py-2 px-3">Delivery Date</th>
                      {selected.kind === "subcontract" && <th className="py-2 px-3">GIN</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.lines || []).map((l, idx) => {
                      const p = linePricing[idx];
                      return (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-2 px-3 mono">{lineCode(l)}</td>
                          <td className="py-2 px-3 text-slate-700">
                            {lineName(l)}
                            {l.is_manual && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-700" data-testid={`dc-line-manual-${idx}`}>
                                one-time{l.rate != null ? ` · rate ${Number(l.rate).toFixed(2)}` : ""}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 mono font-semibold text-right">{l.qty}{l.is_manual && l.uom ? ` ${l.uom}` : ""}</td>
                          {showCumulative && (
                            <>
                              <td className="py-2 px-3 mono text-right">{l.ordered_qty ?? "—"}</td>
                              <td className="py-2 px-3 mono text-right" data-testid={`dc-line-cumulative-${idx}`}>{l.cumulative_qty ?? "—"}</td>
                              <td className={`py-2 px-3 mono text-right ${l.balance_qty > 0 ? "text-amber-700" : "text-slate-500"}`}>{l.balance_qty ?? "—"}</td>
                            </>
                          )}
                          {selected.kind === "customer" && (
                            <>
                              {pricingDraft ? (
                                ["rate", "discount_pct", "gst_pct"].map((f) => (
                                  <td key={f} className="py-1 px-2 text-right">
                                    <Input
                                      type="number" min="0" step="0.01"
                                      className="h-8 w-24 text-right mono ml-auto"
                                      value={pricingDraft[idx]?.[f] ?? ""}
                                      onChange={(e) => setDraftField(idx, f, e.target.value)}
                                      data-testid={`dc-line-${f}-input-${idx}`}
                                    />
                                  </td>
                                ))
                              ) : (
                                <>
                                  <td className="py-2 px-3 mono text-right" data-testid={`dc-line-rate-${idx}`}>
                                    {p ? Number(p.rate || 0).toFixed(2) : "—"}
                                  </td>
                                  <td className="py-2 px-3 mono text-right" data-testid={`dc-line-disc-${idx}`}>
                                    {p ? `${Number(p.discount_pct || 0).toFixed(2)}%` : "—"}
                                  </td>
                                  <td className="py-2 px-3 mono text-right" data-testid={`dc-line-gst-${idx}`}>
                                    {p ? `${Number(p.gst_pct || 0).toFixed(2)}%` : "—"}
                                  </td>
                                </>
                              )}
                              <td className="py-2 px-3 mono text-right font-semibold" data-testid={`dc-line-amount-${idx}`}>
                                {p ? Number(p.amount || 0).toFixed(2) : "—"}
                                {p?.overridden && (
                                  <div className="text-[10px] font-normal text-amber-700 whitespace-nowrap" title="Rate / Disc % / GST % edited on this DC — differs from the Sales Order">
                                    edited
                                    {pricingCanEdit && !pricingDraft && (
                                      <button type="button" className="ml-1 underline" onClick={() => resetLinePricing(idx)} data-testid={`dc-line-pricing-reset-${idx}`}>
                                        reset
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </>
                          )}
                          <td className="py-2 px-3 mono text-xs text-slate-700" data-testid={`dc-line-delivery-${idx}`}>
                            {l.delivery_date ? new Date(l.delivery_date).toLocaleDateString() : "—"}
                          </td>
                          {selected.kind === "subcontract" && (
                            <td className="py-2 px-3 text-xs" data-testid={`dc-line-gin-${idx}`}>
                              {isDcLineReceived(selected, l)
                                ? <span className="mono text-emerald-700">{dcLineGinCode(selected, l) || "Received"}</span>
                                : dcLineReceivedQty(l) > 0
                                  ? <span className="text-amber-700">
                                      Received {fmtQty(dcLineReceivedQty(l))} on <span className="mono">{dcLineGinCode(selected, l)}</span> · {fmtQty(dcLinePendingQty(l))} pending
                                    </span>
                                  : <span className="text-amber-700">Pending</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-xs text-slate-500">
                {selected.kind === "subcontract"
                  ? <>Vendor address: {(selected.vendor_is_manual ? selected.vendor_address : supMap[selected.vendor_id]?.address) || "—"}</>
                  : <>Customer address: {custMap[selected.customer_id]?.address || "—"}</>}
              </div>
            </div>
          )}
        </div>
      </div>

      <PdiReportDialog dc={selected} open={pdiOpen} onOpenChange={setPdiOpen}
        onChanged={(r) => setPdiStatus({ status: r.status, report_no: r.report_no, approved_by_name: r.approved_by_name, approval_date: r.approval_date })} />

      <Sheet open={receiveOpen} onOpenChange={setReceiveOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="dc-receive-sheet">
          <SheetHeader><SheetTitle>Receive Goods — {selected?.code}</SheetTitle></SheetHeader>
          {selected && (
            <div className="mt-6 space-y-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                <div><span className="text-slate-500">Vendor:</span> <strong>{selected.vendor_name || "—"}</strong></div>
              </div>
              {(selected.lines || []).map((l, idx) => (
                <div key={idx} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`dc-receive-line-${idx}`}>
                  <div className="text-xs">
                    <span className="text-slate-500">Part:</span> <strong>{lineName(l)}</strong> <span className="mono text-slate-500">({lineCode(l)})</span>
                    <span className="text-slate-500 ml-2">Sent Qty:</span> <span className="mono font-semibold">{l.qty ?? "—"}</span>
                    {dcLineReceivedQty(l) > 0 && (
                      <>
                        <span className="text-slate-500 ml-2">Received:</span> <span className="mono">{fmtQty(dcLineReceivedQty(l))}</span>
                        <span className="text-slate-500 ml-2">Remaining:</span> <span className="mono font-semibold" data-testid={`dc-receive-remaining-${idx}`}>{fmtQty(dcLinePendingQty(l))}</span>
                      </>
                    )}
                  </div>
                  {isDcLineReceived(selected, l) ? (
                    <div className="text-xs text-emerald-700">
                      Fully received on GIN <span className="mono">{dcLineGinCode(selected, l)}</span>
                    </div>
                  ) : (
                  <div>
                    <Label>Received Quantity * <span className="text-[11px] font-normal text-slate-500">(max {fmtQty(dcLinePendingQty(l))})</span></Label>
                    <Input
                      type="number"
                      min="0"
                      max={dcLinePendingQty(l)}
                      value={receiveForm.received_qtys[idx] ?? ""}
                      onChange={(e) => setReceiveForm((f) => ({ ...f, received_qtys: f.received_qtys.map((q, i) => (i === idx ? e.target.value : q)) }))}
                      data-testid={idx === 0 ? "dc-received-qty-input" : `dc-received-qty-input-${idx}`}
                    />
                    {!(l.is_manual && !l.item_id) && (
                      <div className="mt-2">
                        <ReceivedPartPicker
                          value={receiveForm.received_item_ids?.[idx] || ""}
                          items={items}
                          onPick={(v) => setReceiveForm((f) => ({ ...f, received_item_ids: f.received_item_ids.map((x, i) => (i === idx ? v : x)) }))}
                          targetStore={receiveForm.target_stores?.[idx] || ""}
                          onTargetStore={(v) => setReceiveForm((f) => ({ ...f, target_stores: (f.target_stores || []).map((x, i) => (i === idx ? v : x)) }))}
                          testId={idx === 0 ? "dc-received-part" : `dc-received-part-${idx}`}
                        />
                      </div>
                    )}
                  </div>
                  )}
                </div>
              ))}
              <div className="text-[11px] text-slate-500">
                A GIN will be created for the lines with a received qty, and a Quality Inspection entry will be queued automatically.
                Any qty not received now stays pending — the DC stays open for another GIN until its full qty has been received.
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={receiveForm.notes} onChange={(e) => setReceiveForm((f) => ({ ...f, notes: e.target.value }))} data-testid="dc-receive-notes-input" />
              </div>
            </div>
          )}
          <SheetFooter className="mt-6">
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitReceive} disabled={busy} data-testid="dc-receive-submit">
              Receive &amp; Generate GIN
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Reverse & Delete confirmation — undoes live stock + order-status effects, not just a record delete */}
      <Dialog open={reverseOpen} onOpenChange={setReverseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reverse &amp; delete {selected?.code}?</DialogTitle>
            <DialogDescription>
              This undoes what dispatching this DC did — the stock it debited will be credited back
              {selected?.kind === "subcontract" ? " to the stage store, and the job's remaining quantity restored" : " and the linked Sales Order's delivered quantity/status reverted"}
              — then permanently deletes the DC. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={() => submitReverse(selected)} disabled={busy} data-testid="dc-reverse-confirm">
              <RotateCcw className="w-4 h-4 mr-1.5" />Reverse &amp; Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
